import { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { AirplaneTilt, Buildings } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PACKAGES = [
    { value: 'economy', label: 'Economy Class', lessons: 25 },
    { value: 'business', label: 'Business Class', lessons: 35 },
    { value: 'first_class', label: 'First Class', lessons: 45 },
    { value: 'spoedopleiding_5', label: 'Spoedopleiding 5 uur', lessons: 5, group: 'spoedopleiding' },
    { value: 'spoedopleiding_10', label: 'Spoedopleiding 10 uur', lessons: 10, group: 'spoedopleiding' },
    { value: 'spoedopleiding_16_5', label: 'Spoedopleiding 16,5 uur', lessons: 16.5, group: 'spoedopleiding' },
    { value: 'spoedopleiding_25', label: 'Spoedopleiding 25 uur', lessons: 25, group: 'spoedopleiding' },
];

const EXTERNAL_PACKAGE = 'nationale_rijschool';

export default function AddStudentModal({ onClose, onSuccess }) {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [notes, setNotes] = useState('');
    const [packageType, setPackageType] = useState('');
    const [showSpoedOpties, setShowSpoedOpties] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const isExternal = packageType === EXTERNAL_PACKAGE;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await axios.post(`${API}/students`, {
                name,
                email: isExternal ? null : email,
                phone,
                start_date: startDate,
                notes,
                package_type: packageType,
            }, { withCredentials: true });
            onSuccess();
        } catch (err) {
            setError(err.response?.data?.detail || 'Er is iets misgegaan');
        } finally {
            setLoading(false);
        }
    };

    const selectedPkg = PACKAGES.find(p => p.value === packageType);
    void selectedPkg; // reserved for future preview

    return (
        <Dialog open={true} onOpenChange={onClose}>
            <DialogContent
                className="sm:max-w-lg bg-[#2D3748] border-[#4A5568] text-white p-0 gap-0 max-h-[90vh] flex flex-col overflow-hidden max-w-[calc(100vw-24px)]"
            >
                <DialogHeader className="px-4 sm:px-6 pt-5 pb-3 border-b border-[#4A5568] flex-shrink-0">
                    <DialogTitle className="text-xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Nieuwe Leerling Toevoegen
                    </DialogTitle>
                </DialogHeader>

                {error && (
                    <div className="mx-4 sm:mx-6 mt-3 p-3 bg-red-900/50 border border-red-700 rounded-md text-red-300 text-sm flex-shrink-0">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
                    <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
                    <div className={`grid gap-4 ${isExternal ? 'grid-cols-1' : 'grid-cols-2'}`}>
                        <div className="space-y-2">
                            <Label className="text-gray-300">Naam *</Label>
                            <Input
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Volledige naam"
                                required
                                className="bg-[#1A202C] border-[#4A5568] text-white placeholder:text-gray-500"
                                data-testid="add-student-name-input"
                            />
                        </div>
                        {!isExternal && (
                            <div className="space-y-2">
                                <Label className="text-gray-300">Email *</Label>
                                <Input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="leerling@email.nl"
                                    required={!isExternal}
                                    className="bg-[#1A202C] border-[#4A5568] text-white placeholder:text-gray-500"
                                    data-testid="add-student-email-input"
                                />
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label className="text-gray-300">Telefoonnummer</Label>
                            <Input
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                placeholder="06-12345678"
                                className="bg-[#1A202C] border-[#4A5568] text-white placeholder:text-gray-500"
                                data-testid="add-student-phone-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-gray-300">Startdatum</Label>
                            <Input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="bg-[#1A202C] border-[#4A5568] text-white"
                                data-testid="add-student-date-input"
                            />
                        </div>
                    </div>

                    {/* Package Selection */}
                    <div className="space-y-3">
                        <Label className="text-gray-300">Lespakket *</Label>
                        
                        {/* Main packages */}
                        <div className="grid grid-cols-2 gap-3">
                            {PACKAGES.filter(p => !p.group).map(pkg => (
                                <button
                                    key={pkg.value}
                                    type="button"
                                    onClick={() => { setPackageType(pkg.value); setShowSpoedOpties(false); }}
                                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                                        packageType === pkg.value
                                            ? 'border-[#D4A537] bg-[#D4A537]/10'
                                            : 'border-[#4A5568] bg-[#1A202C] hover:border-[#D4A537]/50'
                                    }`}
                                    data-testid={`package-${pkg.value}`}
                                >
                                    <div className="flex items-center justify-between mb-1">
                                        <span className={`font-semibold ${packageType === pkg.value ? 'text-[#D4A537]' : 'text-white'}`}>
                                            {pkg.label}
                                        </span>
                                        <AirplaneTilt 
                                            size={20} 
                                            weight="fill"
                                            className={packageType === pkg.value ? 'text-[#D4A537]' : 'text-gray-500'}
                                        />
                                    </div>
                                    <div className="text-sm text-gray-400">
                                        {pkg.lessons} lessen
                                    </div>
                                </button>
                            ))}

                            {/* Spoedopleiding button */}
                            <button
                                type="button"
                                onClick={() => { setShowSpoedOpties(!showSpoedOpties); }}
                                className={`p-4 rounded-lg border-2 text-left transition-all col-span-2 ${
                                    packageType.startsWith('spoedopleiding')
                                        ? 'border-[#D4A537] bg-[#D4A537]/10'
                                        : 'border-[#4A5568] bg-[#1A202C] hover:border-[#D4A537]/50'
                                }`}
                                data-testid="package-spoedopleiding"
                            >
                                <div className="flex items-center justify-between mb-1">
                                    <span className={`font-semibold ${packageType.startsWith('spoedopleiding') ? 'text-[#D4A537]' : 'text-white'}`}>
                                        Spoedopleiding Nederland
                                    </span>
                                    <AirplaneTilt 
                                        size={20} 
                                        weight="fill"
                                        className={packageType.startsWith('spoedopleiding') ? 'text-[#D4A537]' : 'text-gray-500'}
                                    />
                                </div>
                                <div className="text-sm text-gray-400">
                                    {packageType.startsWith('spoedopleiding') 
                                        ? PACKAGES.find(p => p.value === packageType)?.label
                                        : 'Klik om uren te kiezen'}
                                </div>
                            </button>

                            {/* Nationale Rijschool — external students (planning only) */}
                            <button
                                type="button"
                                onClick={() => { setPackageType(EXTERNAL_PACKAGE); setShowSpoedOpties(false); }}
                                className={`p-4 rounded-lg border-2 text-left transition-all col-span-2 ${
                                    isExternal
                                        ? 'border-[#F97316] bg-[#F97316]/10'
                                        : 'border-[#4A5568] bg-[#1A202C] hover:border-[#F97316]/60'
                                }`}
                                data-testid="package-nationale-rijschool"
                            >
                                <div className="flex items-center justify-between mb-1">
                                    <span className={`font-semibold ${isExternal ? 'text-[#F97316]' : 'text-white'}`}>
                                        Nationale Rijschool
                                    </span>
                                    <Buildings
                                        size={20}
                                        weight="fill"
                                        className={isExternal ? 'text-[#F97316]' : 'text-gray-500'}
                                    />
                                </div>
                                <div className="text-sm text-gray-400">
                                    Alleen voor planning — geen leskaart, voortgang of inlog
                                </div>
                            </button>
                        </div>

                        {/* Spoedopleiding sub-options */}
                        {showSpoedOpties && (
                            <div className="grid grid-cols-2 gap-3 ml-4 pl-4 border-l-2 border-[#D4A537]/30">
                                {PACKAGES.filter(p => p.group === 'spoedopleiding').map(pkg => (
                                    <button
                                        key={pkg.value}
                                        type="button"
                                        onClick={() => { setPackageType(pkg.value); }}
                                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                                            packageType === pkg.value
                                                ? 'border-[#D4A537] bg-[#D4A537]/10'
                                                : 'border-[#4A5568] bg-[#1A202C] hover:border-[#D4A537]/50'
                                        }`}
                                        data-testid={`package-${pkg.value}`}
                                    >
                                        <span className={`font-semibold ${packageType === pkg.value ? 'text-[#D4A537]' : 'text-white'}`}>
                                            {pkg.label}
                                        </span>
                                        <div className="text-sm text-gray-400 mt-0.5">
                                            {pkg.lessons} lessen
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label className="text-gray-300">Notities</Label>
                        <Textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Eventuele opmerkingen over de leerling..."
                            rows={2}
                            className="bg-[#1A202C] border-[#4A5568] text-white placeholder:text-gray-500"
                            data-testid="add-student-notes-input"
                        />
                    </div>

                    {isExternal ? (
                        <p className="text-xs text-[#FB923C]">
                            Externe leerling van Nationale Rijschool — verschijnt alleen in jouw agenda en in &quot;Volgende leerling&quot;. Geen login of leskaart.
                        </p>
                    ) : (
                        <p className="text-xs text-gray-500">
                            De leerling kan inloggen met dit email en wachtwoord: Student2024!
                        </p>
                    )}
                    </div>

                    <div className="border-t border-[#4A5568] bg-[#2D3748] px-4 sm:px-6 py-3 flex justify-end gap-3 flex-shrink-0">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            className="border-[#4A5568] text-gray-300 hover:bg-[#3D4A5C]"
                            data-testid="add-student-cancel-button"
                        >
                            Annuleren
                        </Button>
                        <Button
                            type="submit"
                            disabled={loading || !packageType}
                            className={`${isExternal ? 'bg-[#F97316] hover:bg-[#FB923C]' : 'bg-[#D4A537] hover:bg-[#E5B84A]'} text-[#1A202C] font-semibold relative z-50`}
                            data-testid="add-student-submit-button"
                        >
                            {loading ? 'Bezig...' : 'Toevoegen'}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
